import React, { useEffect, useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { App, Button } from 'antd';
import Tooltip from '../common/Tooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useTranslation } from 'react-i18next';
import { useStore } from '../../store/store';
import { pageBackground, roundButtonWidth } from '../../constants/constants';
import { useOverlay } from '../../contexts/OverlayContext';

const roundButtonStyle = {
  width: roundButtonWidth,
  height: roundButtonWidth,
  minWidth: roundButtonWidth,
  minHeight: roundButtonWidth,
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
};

const getSelectedContainersBounds = (containerIds) => {
  const rects = containerIds
    .map((id) => document.getElementById(id)?.getBoundingClientRect())
    .filter((rect) => rect && rect.width > 0 && rect.height > 0);

  if (rects.length === 0) return null;

  return {
    right: Math.max(...rects.map((rect) => rect.right)),
  };
};

const MultiContainerActionsToolbar = () => {
  const { t } = useTranslation();
  const { modal } = App.useApp();
  const { overlayProps } = useOverlay();
  const shownStatus = overlayProps?.left?.shownStatus;

  const {
    selectedContainerIds,
    containers,
    removeSelectedContainers,
    updateContainer,
    currentDocument,
    currentTemplate,
    currentProposal,
    configuration,
    proposalList,
  } = useStore((state) => ({
    selectedContainerIds: state.selectedContainerIds,
    containers: state.containers,
    removeSelectedContainers: state.removeSelectedContainers,
    updateContainer: state.updateContainer,
    currentDocument: state.currentDocument,
    currentTemplate: state.currentTemplate,
    currentProposal: state.currentProposal,
    configuration: state.configuration,
    proposalList: state.proposalList,
  }));

  const [hoveredButton, setHoveredButton] = useState(null);
  const [toolbarPosition, setToolbarPosition] = useState(null);

  const selectedContainers = useMemo(
    () =>
      (selectedContainerIds ?? [])
        .map((id) => containers.find((c) => c.id === id))
        .filter(Boolean),
    [selectedContainerIds, containers]
  );

  const count = selectedContainers.length;
  const isMultiSelection = count > 1;
  const isTemplate = currentDocument?.type === 'template';
  const canDelete = isTemplate || configuration?.unlockProposal;
  const showNoRepeat =
    isTemplate &&
    currentTemplate?.templatable !== 'offer' &&
    selectedContainers.length > 0;
  const showEditableLock =
    currentProposal?.id === null ||
    selectedContainers.some((c) => c.editable === false);
  const canEditLock = selectedContainers.every((c) => !c?.from_linked_section);
  // Même règle que le verrou : le contenu d'une section liée se règle dans sa source.
  const canEditNoRepeat = canEditLock;

  const allNoRepeat = selectedContainers.every((c) => c.noRepeat);
  const allLocked = selectedContainers.every((c) => c.editable === false);

  const isVisible =
    !proposalList &&
    isMultiSelection &&
    shownStatus !== 'variables';

  useEffect(() => {
    if (!isVisible) {
      setToolbarPosition(null);
      return;
    }

    const updatePosition = () => {
      const bounds = getSelectedContainersBounds(selectedContainerIds);
      if (!bounds) {
        setToolbarPosition(null);
        return;
      }

      setToolbarPosition({
        left: bounds.right + 8,
        top: window.innerHeight / 2,
      });
    };

    updatePosition();

    const scrollContainer = document.querySelector('.no-scrollbar');
    window.addEventListener('resize', updatePosition);
    scrollContainer?.addEventListener('scroll', updatePosition);

    return () => {
      window.removeEventListener('resize', updatePosition);
      scrollContainer?.removeEventListener('scroll', updatePosition);
    };
  }, [isVisible, selectedContainerIds, configuration?.zoom]);

  if (!isVisible || !toolbarPosition) {
    return null;
  }

  const confirm = ({ title, onOk, okType = 'primary' }) => {
    modal.confirm({
      title,
      okText: t('yes'),
      cancelText: t('cancel'),
      okType,
      onOk,
    });
  };

  const handleDelete = () => {
    if (!canDelete) return;
    const ids = [...selectedContainerIds];
    confirm({
      title:
        ids.length > 1
          ? t('delete-blocks-confirm', { count: ids.length })
          : t('delete-block-confirm'),
      okType: 'danger',
      onOk: () => removeSelectedContainers(ids),
    });
  };

  const handleNoRepeat = () => {
    if (!canEditNoRepeat) return;
    const nextValue = !allNoRepeat;
    confirm({
      title: nextValue
        ? `Ne pas répéter ${count} lignes ?`
        : `Répéter ${count} lignes lors d'une injection en boucle ?`,
      onOk: () => {
        selectedContainerIds.forEach((id) => {
          updateContainer({
            id,
            fn: (container) => {
              container.noRepeat = nextValue;
            },
          });
        });
      },
    });
  };

  const handleEditableLock = () => {
    if (!canEditLock) return;
    const lockAll = !allLocked;
    confirm({
      title: lockAll
        ? `Bloquer l'édition de ${count} lignes ?`
        : `Rendre ${count} lignes éditables ?`,
      onOk: () => {
        selectedContainerIds.forEach((id) => {
          updateContainer({
            id,
            fn: (container) => {
              container.editable = !lockAll;
            },
          });
        });
      },
    });
  };

  const toolbar = (
    <div
      data-outside-ignore="true"
      style={{
        position: 'fixed',
        left: toolbarPosition.left,
        top: toolbarPosition.top,
        transform: 'translateY(-50%)',
        display: 'grid',
        gridTemplateColumns: 'repeat(2, auto)',
        gap: 8,
        zIndex: 1003,
      }}
    >
      {canDelete && (
        <Tooltip
          open={hoveredButton === 'delete'}
          placement="left"
          title={t('container-deleteTooltip')}
        >
          <div
            onMouseEnter={() => setHoveredButton('delete')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <Button
              shape="circle"
              size={14}
              data-role={pageBackground}
              onClick={handleDelete}
              style={{ ...roundButtonStyle, color: 'red' }}
              icon={
                <FontAwesomeIcon
                  icon={icon({ name: 'trash', style: 'light' })}
                  style={{ margin: 0, fontSize: 15 }}
                />
              }
            />
          </div>
        </Tooltip>
      )}
      {showNoRepeat && (
        <Tooltip
          open={hoveredButton === 'noRepeat'}
          placement="left"
          title={t(
            !canEditNoRepeat
              ? 'container-no-repeat-linked'
              : allNoRepeat
                ? 'container-repeat-enable'
                : 'container-no-repeat'
          )}
        >
          <div
            onMouseEnter={() => setHoveredButton('noRepeat')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <Button
              shape="circle"
              size={14}
              data-role={pageBackground}
              onClick={handleNoRepeat}
              disabled={!canEditNoRepeat}
              style={roundButtonStyle}
              icon={
                <span
                  className="fa-layers fa-fw"
                  style={{ color: allNoRepeat ? '#cf1322' : undefined }}
                >
                  <FontAwesomeIcon
                    icon={icon({ name: 'arrows-repeat', style: 'light' })}
                    style={{ fontSize: 15 }}
                  />
                  {allNoRepeat && (
                    <FontAwesomeIcon
                      icon={icon({ name: 'slash', style: 'light' })}
                      style={{ fontSize: 15 }}
                    />
                  )}
                </span>
              }
            />
          </div>
        </Tooltip>
      )}
      {showEditableLock && (
        <Tooltip
          open={hoveredButton === 'lock'}
          placement="left"
          title={t(allLocked ? 'unblock-editable-container' : 'block-editable-container')}
        >
          <div
            onMouseEnter={() => setHoveredButton('lock')}
            onMouseLeave={() => setHoveredButton(null)}
          >
            <Button
              shape="circle"
              size={14}
              data-role={pageBackground}
              disabled={!canEditLock}
              onClick={handleEditableLock}
              style={roundButtonStyle}
              icon={
                allLocked ? (
                  <FontAwesomeIcon
                    icon={icon({ name: 'lock-keyhole', style: 'light' })}
                    style={{ margin: 0, fontSize: 15 }}
                  />
                ) : (
                  <FontAwesomeIcon
                    icon={icon({ name: 'lock-keyhole-open', style: 'light' })}
                    style={{ margin: 0, fontSize: 15 }}
                  />
                )
              }
            />
          </div>
        </Tooltip>
      )}
    </div>
  );

  if (typeof document === 'undefined') return null;
  return ReactDOM.createPortal(toolbar, document.body);
};

export default MultiContainerActionsToolbar;
